/**
 * VendorItemFormModal - Create/Edit vendor catalog items
 * Part of the complete product lifecycle: Vendor → Vendor Item → Product
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, Box, Save, X, Plus, Trash2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VendorItemFormModal = ({ open, onOpenChange, itemId = null, vendorSources = [], categories = [], onSave }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    vendor_source_id: '',
    vendor_sku: '',
    name: '',
    description: '',
    category_code: '',
    cost_price: 0,
    msrp: null,
    min_order_qty: 1,
    lead_time_days: 7,
    is_active: true,
    variants: []
  });

  const isEditMode = !!itemId;

  // Load item function
  const loadItem = async (id) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/catalog/vendor-items/${id}`);
      if (res.ok) {
        const data = await res.json();
        setFormData({
          vendor_source_id: data.vendor_source_id || '',
          vendor_sku: data.vendor_sku || '',
          name: data.name || '',
          description: data.description || '',
          category_code: data.category_code || '',
          cost_price: data.cost_price || 0,
          msrp: data.msrp || null,
          min_order_qty: data.min_order_qty || 1,
          lead_time_days: data.lead_time_days || 7,
          is_active: data.is_active !== false,
          variants: data.variants || []
        });
      }
    } catch (error) {
      console.error('Error loading vendor item:', error);
      toast.error('Failed to load vendor item');
    }
    setLoading(false);
  };

  // Load item data if editing
  useEffect(() => {
    if (open && itemId) {
      loadItem(itemId);
    } else if (open && !itemId) {
      // Reset form for new item
      setFormData({
        vendor_source_id: vendorSources[0]?.id || '',
        vendor_sku: '',
        name: '',
        description: '',
        category_code: '',
        cost_price: 0,
        msrp: null,
        min_order_qty: 1,
        lead_time_days: 7,
        is_active: true,
        variants: []
      });
    }
  }, [open, itemId, vendorSources]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Variant management
  const addVariant = () => {
    setFormData(prev => ({
      ...prev,
      variants: [...prev.variants, {
        sku_suffix: '',
        name: '',
        attributes: {},
        cost: 0
      }]
    }));
  };

  const updateVariant = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.map((v, i) => i === index ? { ...v, [field]: value } : v)
    }));
  };

  const removeVariant = (index) => {
    setFormData(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!formData.vendor_source_id) {
      toast.error('Please select a vendor');
      return;
    }
    if (!formData.vendor_sku) {
      toast.error('Vendor SKU is required');
      return;
    }
    if (!formData.name) {
      toast.error('Item name is required');
      return;
    }

    setSaving(true);
    try {
      const url = isEditMode 
        ? `${API_URL}/api/catalog/vendor-items/${itemId}`
        : `${API_URL}/api/catalog/vendor-items`;
      
      // Map vendor_source_id to vendor_id for API compatibility
      const payload = {
        ...formData,
        vendor_id: formData.vendor_source_id
      };
      delete payload.vendor_source_id;
      
      const res = await fetch(url, {
        method: isEditMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(isEditMode ? 'Vendor item updated' : 'Vendor item created');
        onSave?.(data.vendor_item || data);
        onOpenChange(false);
      } else {
        toast.error(data.detail || 'Failed to save vendor item');
      }
    } catch (error) {
      toast.error('Error saving vendor item');
      console.error(error);
    }
    setSaving(false);
  };

  // Get unique category codes from categories
  const categoryCodes = [...new Set(categories.map(c => c.code).filter(Boolean))];
  const selectedVendor = vendorSources.find(v => v.id === formData.vendor_source_id);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="vendor-item-form-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Box className="w-5 h-5" />
            {isEditMode ? 'Edit Vendor Item' : 'Add Vendor Catalog Item'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Vendor & SKU */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="vendor">Vendor *</Label>
                <Select 
                  value={formData.vendor_source_id} 
                  onValueChange={(v) => handleChange('vendor_source_id', v)}
                >
                  <SelectTrigger data-testid="vendor-select">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendorSources.map(vendor => (
                      <SelectItem key={vendor.id} value={vendor.id}>
                        {vendor.name} ({vendor.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedVendor && (
                  <p className="text-xs text-gray-500 mt-1">
                    Type: {selectedVendor.vendor_type} | Markup: {selectedVendor.default_markup_percent}%
                  </p>
                )}
              </div>
              
              <div>
                <Label htmlFor="vendor_sku">Vendor SKU *</Label>
                <Input
                  id="vendor_sku"
                  value={formData.vendor_sku}
                  onChange={(e) => handleChange('vendor_sku', e.target.value)}
                  placeholder="e.g., CRW-CRY-001"
                  className="font-mono"
                  data-testid="vendor-sku-input"
                />
                <p className="text-xs text-gray-500 mt-1">SKU from the vendor&apos;s catalog</p>
              </div>
            </div>

            {/* Name & Category */}
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label htmlFor="name">Item Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="e.g., Crystal Star Award 6 inch"
                  data-testid="item-name-input"
                />
              </div>
              
              <div>
                <Label htmlFor="category_code">Category Code</Label>
                <Select 
                  value={formData.category_code} 
                  onValueChange={(v) => handleChange('category_code', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoryCodes.map(code => (
                      <SelectItem key={code} value={code}>
                        {code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Item description from vendor..."
                rows={2}
              />
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label htmlFor="cost_price">Cost Price *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    id="cost_price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.cost_price}
                    onChange={(e) => handleChange('cost_price', parseFloat(e.target.value) || 0)}
                    className="pl-7"
                    data-testid="cost-price-input"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="msrp">MSRP</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    id="msrp"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.msrp || ''}
                    onChange={(e) => handleChange('msrp', parseFloat(e.target.value) || null)}
                    className="pl-7"
                    placeholder="Optional"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="min_qty">Min Order Qty</Label>
                <Input
                  id="min_qty"
                  type="number"
                  min="1"
                  value={formData.min_order_qty}
                  onChange={(e) => handleChange('min_order_qty', parseInt(e.target.value) || 1)}
                />
              </div>
              
              <div>
                <Label htmlFor="lead_time">Lead Time (days)</Label>
                <Input
                  id="lead_time"
                  type="number"
                  min="1"
                  value={formData.lead_time_days}
                  onChange={(e) => handleChange('lead_time_days', parseInt(e.target.value) || 1)}
                />
              </div>
            </div>

            {/* Variants Section */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <Label className="text-sm font-medium">Variants</Label>
                  <p className="text-xs text-gray-500">Add size/color variants with different costs</p>
                </div>
                <Button size="sm" variant="outline" onClick={addVariant}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Variant
                </Button>
              </div>

              {formData.variants.length === 0 ? (
                <p className="text-sm text-gray-400 italic py-2">No variants - using base cost price</p>
              ) : (
                <div className="space-y-2">
                  {formData.variants.map((variant, index) => (
                    <Card key={index} className="p-3">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="text-xs">{index + 1}</Badge>
                        <Input
                          value={variant.sku_suffix}
                          onChange={(e) => updateVariant(index, 'sku_suffix', e.target.value)}
                          placeholder="SKU suffix (e.g., SM)"
                          className="w-24 font-mono text-sm"
                        />
                        <Input
                          value={variant.name}
                          onChange={(e) => updateVariant(index, 'name', e.target.value)}
                          placeholder="Variant name (e.g., Small)"
                          className="flex-1 text-sm"
                        />
                        <div className="relative w-24">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            value={variant.cost || ''}
                            onChange={(e) => updateVariant(index, 'cost', parseFloat(e.target.value) || 0)}
                            className="pl-5 text-sm"
                            placeholder="Cost"
                          />
                        </div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeVariant(index)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </div>

            {/* Active Status */}
            <div className="flex items-center gap-2 pt-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
                className="rounded"
              />
              <Label htmlFor="is_active">Active (available for ordering)</Label>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} data-testid="save-vendor-item-btn">
                {saving ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><Save className="w-4 h-4 mr-2" /> {isEditMode ? 'Update' : 'Create'} Item</>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default VendorItemFormModal;
